import { Injectable, OnDestroy, Renderer2, RendererFactory2 } from '@angular/core';
import { Store } from '@ngrx/store';
import { ContextType } from '@charlesschwab/as-ui-core';
import { CoreService, ICurrentContext, ICurrentUserContext } from '@charlesschwab/sac-ui-core';
import { Subject, Observable, fromEventPattern, combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AccountInfoActions } from '../../account-info-header/actions';
import { ChannelCode, EnvironmentHeader, JfkEnvironment } from '../types/JfkEnvironment';
import { SharedActions } from '../actions';
import { ValidateWireFlagsService } from './validate-WireFlags.service';

@Injectable()
export class IntegrationService implements OnDestroy {
  private spinnerElement = 'global-loadingMessage'
  currentContext: ICurrentContext = {
    contextId: 0,
    contextType: ContextType.Unknown,
    firmId: 0,
    firmName: '',
    title: '',
  };
  currentUserContext: ICurrentUserContext = {
    altId: '',
    isDbsUser: false,
    isEmployee: false,
    isFsa: false,
    isKaspickUser: false,
    userName: '',
    firmId: 0,
  };

  private _destroy$ = new Subject<void>();

  public accountUpdated$: Observable<CustomEvent>;

  constructor(private store: Store,
    private rendererFactory2: RendererFactory2,
    private coreService: CoreService,
    private validateWireFlagsService: ValidateWireFlagsService) {
    this.coreService.currentUserContextUpdated$
      .pipe(takeUntil(this._destroy$))
      .subscribe((userContext) => {
        this.loadCurrentUserContext();
      });
    this.loadCurrentUserContext();

    this.coreService.currentContextUpdated$
      .pipe(takeUntil(this._destroy$))
      .subscribe((context) => {
       this.loadCurrentContext();
      });
    // set the context to fire the observable above since it is based on a Subject and not a BehaviorSubject
    this.loadCurrentContext();


    const renderer = this.rendererFactory2.createRenderer(null, null);

    this.createAccountUpdatedObservable(renderer);
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  showSpinner = () => {
    const show = document.getElementById(this.spinnerElement);
    if (show !== null && show !== undefined) {
      show.style.display = 'block';
    }
  };

  hideSpinner = () => {
    const hide = document.getElementById(this.spinnerElement);
    if (hide !== null && hide !== undefined) {
      hide.style.display = 'none';
    }
  };

  loadCurrentUserContext(): void {
    if (this.coreService.currentUserContext) {
      this.currentUserContext = this.coreService.currentUserContext;
    }

    this.store.dispatch(
      AccountInfoActions.loadIsDbsUserAndIsEmployee({
        isDbsUser: this.currentUserContext.isDbsUser ?? false,
        isEmployee: this.currentUserContext.isEmployee
      })
    )

    this.store.dispatch(
      AccountInfoActions.loadUsername({
        userName: this.currentUserContext.userName
      })
    )
  }

  loadCurrentContext(){
    if (this.coreService.currentContext) {
      this.currentContext = this.coreService.currentContext;
    }

    this.store.dispatch(SharedActions.currentContextUpdated());
    this.store.dispatch(AccountInfoActions.updateUserContext({ context: this.currentContext?.contextType}))

    if (this.currentContext?.contextType === ContextType.Account 
      || (this.currentContext?.contextType === ContextType.PALAccount 
        && this.validateWireFlagsService.enablePalWireCreateTransaction())) {
      this.store.dispatch(
        AccountInfoActions.updateSubAccount({
          accountId: this.currentContext.contextId !== 0 ? this.currentContext.contextId.toString() : '',
        })
      );
    }

    if (this.currentContext?.contextType === ContextType.Master) {
      this.store.dispatch(
        AccountInfoActions.updateMasterAccount({
          masterAccount: this.currentContext.contextId !== 0 ? this.currentContext.contextId.toString() : '',
        })
      );
    }
  }

  setContext(context: ICurrentContext): void {
    this.coreService.currentContext = context;
  }

  get JfkEnv(): JfkEnvironment {
    return {
      BaseUrl: this.coreService.bffGatewayUrl,
      EnvironmentHeader: this.coreService.environment as EnvironmentHeader,
      ChannelCode: this.coreService.channel as ChannelCode,
    }
  }

  private createAccountUpdatedObservable(renderer: Renderer2) {
    let removeAccountUpdatedEventListener: () => void;

    const createAccountUpdatedEventListener = (
      handler: (e: Event) => boolean | void
    ) => {
      removeAccountUpdatedEventListener = renderer.listen("window", "newAccountId", handler);
    };

    this.accountUpdated$ = fromEventPattern<CustomEvent>(createAccountUpdatedEventListener, () =>
      removeAccountUpdatedEventListener()
    ).pipe(takeUntil(this._destroy$));
  }
}